/**
 * VP Thin-Client Dev Fast-Path
 *
 * Flag-gated dev entry that drops a designated dev account STRAIGHT into the
 * game terminal with its already-owned character, skipping world-choice and
 * character-choice. It does NOT invent a parallel connect: it triggers the
 * EXISTING resume path (the `<player>` authority resume-via-owned_name) by
 * driving the same `/api/worlds/join` -> connect flow `/world join` uses, then
 * lets the server's `connected(needs_creation:false, character, spawn_location)`
 * frame + the client resume path (handleStatelessConnection) do the rest. The
 * room-entry look delivers ch0 + phase_clock (REG-2 unlock), identical to
 * spawn/walk arrival.
 *
 * Trigger (spec .claude_specs/engine/vp-thin-client-fastpath/spec.md):
 *   index.html?fastpath=1[&world=<world-handle>]     (the ONLY trigger)
 *
 * ⚠️ THE DEV-ACCOUNT ALLOWLIST IS REMOVED (2026-07-13). It was an IMPLICIT
 * trigger: a bare login from an allowlisted email auto-resumed straight to
 * GAMEPLAY and set `__REAVERS_FASTPATH_ACTIVE__`, which makes `Shell.isActive()`
 * return FALSE — so the entire shell went inert. That silently suppressed, for
 * exactly the accounts we test with:
 *
 *   - the WORLD LIST (the account never reached the lobby),
 *   - the WARMING / QUEUE screens (the shell owns them), and
 *   - every other pre-game screen,
 *
 * dropping the tester into the raw terminal — the surface whose /world slash
 * commands we are retiring. An implicit, email-keyed trigger that hijacks a
 * human's login is exactly the kind of hidden fallback that hides the real
 * product from the people reviewing it.
 *
 * The fast-path remains available, DETERMINISTICALLY and EXPLICITLY, via
 * `?fastpath=1` — which no human types by accident and which the VP harness
 * passes on purpose.
 *
 * Zero real-user impact: a normal player never passes the param, so `maybeRun()`
 * is a no-op for them.
 *
 * Design note: .claude_specs/engine/vp-thin-client-fastpath/design-note.md
 */

import UnifiedAuthService from './unified-auth.js';
import WorldCommands from './world-commands.js';
import WorldList from './world-list.js';

const Fastpath = {
    // ⚠️ NO DEV_ALLOWLIST. `?fastpath=1` is the ONLY trigger — see the header.
    // An implicit email-keyed trigger silently suppressed the shell (world list,
    // warming/queue screens, every pre-game screen) for the very accounts used to
    // review the product.
    _started: false,

    _params() {
        try {
            return new URLSearchParams(window.location.search);
        } catch (_e) {
            return new URLSearchParams('');
        }
    },

    /** True when ?fastpath=1 is present (the deterministic primary trigger). */
    isParamActive() {
        return this._params().get('fastpath') === '1';
    },

    /** The optional world handle from ?world=<handle> (name or world_id). */
    worldHandle() {
        const w = this._params().get('world');
        return w ? w.trim() : null;
    },

    /**
     * Whether the on-load login redirect should be suppressed. ONLY the param
     * suppresses it — at pre-auth redirect time we cannot know the account, so
     * the allowlist path relies on an already-restored session (or a normal
     * one-time login) rather than redirect suppression. Called synchronously by
     * main.js BEFORE UnifiedAuthService.init() so unified-auth's
     * shouldRedirectToLogin() can consult the resulting global flag.
     */
    shouldSuppressLoginRedirect() {
        return this.isParamActive();
    },

    /**
     * Register the fast-path trigger. Without `?fastpath=1` this returns
     * immediately and registers nothing -- there is no implicit path that can
     * turn the fast-path on. When the param IS present, it waits (via
     * onAuthStateChanged, which fires immediately if already authenticated) for
     * auth to arrive, then starts the resume.
     */
    maybeRun() {
        // The ONLY trigger is ?fastpath=1. If it is absent, do not even register
        // the auth listener -- there is no implicit path that can turn this on.
        if (typeof UnifiedAuthService === 'undefined') {
            return;
        }
        if (!this.isParamActive()) {
            return; // every normal login (dev or not) -> the shell owns the screen
        }

        UnifiedAuthService.onAuthStateChanged((user, _token) => {
            if (this._started || !user) {
                return;
            }
            this._started = true;
            // Signal the connect->play shell to yield. The shell reads this flag
            // (plus the ?fastpath=1 param) in Shell.isActive() so it never renders
            // its pre-game overlay over a fast-path resume. Set synchronously here,
            // before _start(), and because Fastpath.maybeRun() is registered BEFORE
            // Shell.init() in main.js this listener wins the auth-fired race.
            if (typeof window !== 'undefined') {
                window.__REAVERS_FASTPATH_ACTIVE__ = true;
            }
            console.log(
                `[fastpath] active (trigger=param, account=${user.email || user.uid}) ` +
                `-- resolving world and auto-resuming into the terminal`
            );
            this._start(user).catch((err) => {
                console.error('[fastpath] failed to auto-resume:', err);
            });
        });
    },

    /**
     * Resolve the target world (name-space, existing /api/worlds/list) and
     * trigger the existing join -> connect -> resume path.
     */
    async _start(_user) {
        const worldId = await this._resolveWorldId();
        if (!worldId) {
            // World-less / ambiguous: do NOT hang and do NOT build a parallel
            // path. Fall through to the normal flow (the input line + /world
            // commands are already live). Logged so a stalled VP run is
            // diagnosable rather than silent.
            console.warn(
                '[fastpath] no world resolved -- falling through to the normal ' +
                'world-menu flow (pass ?world=<handle> to fast-path a specific world)'
            );
            return;
        }

        console.log(`[fastpath] joining world_id=${worldId} (existing join->connect->resume path)`);

        let joinResp;
        try {
            joinResp = await WorldCommands.callWorldAPI('/api/worlds/join', 'POST', {
                world_id: worldId,
                database: WorldCommands.state.selectedDatabase,
            });
        } catch (err) {
            console.error('[fastpath] /api/worlds/join failed:', err);
            return;
        }

        if (!joinResp || !joinResp.success || !joinResp.connection_details) {
            console.warn(
                `[fastpath] join did not return connection_details ` +
                `(error=${joinResp && joinResp.error}) -- falling through`
            );
            return;
        }

        // Reuse the exact connect path /world join uses. The server's
        // `connected` resume frame + handleStatelessConnection drive the
        // WORLD_MENU -> resume_gameplay -> GAMEPLAY transition and the
        // room-entry look (ch0 + phase_clock, REG-2 unlock). We never touch the
        // FSM directly.
        await WorldCommands.connectToGameServer(joinResp.connection_details);
    },

    /**
     * Turn ?world=<handle> into a world_id via the SHARED world-resolve helper
     * (WorldList), the same accessor the shell lobby uses. Resolution is
     * page-independent: a ?world= that sits beyond page 0 of an unsorted list is
     * still found (2026-07-11-013-ENGINE) because WorldList.resolveWorldByName
     * uses the server-side name= filter, not a page-0 scan.
     */
    async _resolveWorldId() {
        const handle = this.worldHandle();
        const database = WorldCommands.state.selectedDatabase;

        if (handle) {
            let match = null;
            try {
                match = await WorldList.resolveWorldByName(handle, { database });
            } catch (err) {
                console.error(`[fastpath] world resolve failed for "${handle}":`, err);
                return null;
            }
            if (match) {
                return match.world_id;
            }
            // Read-honesty: a genuine no-match is surfaced, never a silent
            // page-0 truncation. The caller (_start) logs a fall-through so a
            // stalled resume is diagnosable rather than a mystery DISCONNECTED.
            console.error(
                `[fastpath] ?world="${handle}" not found in any world-list page ` +
                `(server name filter + full scan both missed) -- cannot resume`
            );
            return null;
        }

        // No explicit handle: only auto-pick when unambiguous. Use the FULL list
        // (not just page 0) so "single world" is judged against every world.
        let worlds = [];
        try {
            const res = await WorldList.fetchAllOpenWorlds({ database });
            worlds = res.worlds;
        } catch (err) {
            console.error('[fastpath] world list failed:', err);
            return null;
        }

        if (worlds.length === 0) {
            console.warn('[fastpath] world list is empty');
            return null;
        }
        if (worlds.length === 1) {
            console.log(`[fastpath] single world available -- using "${worlds[0].name}"`);
            return worlds[0].world_id;
        }

        console.warn(
            `[fastpath] no ?world= and ${worlds.length} worlds available; ` +
            `cannot auto-resolve -- pass ?world=<handle>`
        );
        return null;
    },
};

export default Fastpath;
